import { useQuery } from '@tanstack/react-query';

import { companiesService, individualsService, useToast } from '@aslanpay/portal-common-lib';

import { RECONCILIATION_POLL_INTERVAL_MS } from 'app/constants/reconciliations';

import ReconciliationsApiHandler from 'app/services/reconciliationsApiHandler';

import { FetchReconciliationsRequest, ReconciliationRunsPage } from 'app/types/reconciliations';

import { isActiveReconciliationRun } from 'app/utils/reconciliations';

export const RECONCILIATIONS_PAGE_QUERY_KEY = 'fetchReconciliationsPage';
const ALL = 999999;

/**
 * @description Names the companies the runs covered, in one call. Runs are still listed if the names can't be read.
 */
export const fetchCompanyNames = async (companyIds: string[]) => {
  if (companyIds.length === 0) {
    return new Map<string, string>();
  }

  try {
    const companies = await companiesService.fetchCompanies({
      ids: companyIds,
      page: 0,
      size: ALL,
    });
    return new Map(companies.list.map((company) => [company.id, company.name]));
  } catch {
    return new Map<string, string>();
  }
};

/**
 * @description Names the admins who started runs by hand, in one call. Runs are still listed if the names can't be read.
 */
export const fetchAdminNames = async (individualIds: string[]) => {
  if (individualIds.length === 0) {
    return new Map<string, string>();
  }

  try {
    const individuals = await individualsService.fetchIndividuals({
      ids: individualIds,
      page: 0,
      size: ALL,
    });
    return new Map(
      individuals.list.map((individual) => [
        individual.id,
        [individual.firstName, individual.lastName].filter(Boolean).join(' ') || individual.email,
      ])
    );
  } catch {
    return new Map<string, string>();
  }
};

/** Lists reconciliation runs, newest first, refreshing while any of them is still queued or running. */
const useFetchReconciliationsPage = (
  { companyId, status, page, size }: FetchReconciliationsRequest,
  enabled = true
) => {
  const toast = useToast();

  return useQuery<ReconciliationRunsPage, Error>(
    [RECONCILIATIONS_PAGE_QUERY_KEY, companyId, status, page, size],
    async () => {
      const runs = await ReconciliationsApiHandler.listReconciliations({
        companyId,
        status,
        page,
        size,
      });

      const companyIds = [...new Set(runs.list.flatMap((run) => run.companyIds))];
      const adminIds = [
        ...new Set(runs.list.filter((run) => !run.automated).map((run) => run.triggeredBy)),
      ];
      const [companyNames, adminNames] = await Promise.all([
        fetchCompanyNames(companyIds),
        fetchAdminNames(adminIds),
      ]);

      return {
        ...runs,
        list: runs.list.map((run) => ({
          ...run,
          companyNames: run.companyIds
            .map((id) => companyNames.get(id))
            .filter((name): name is string => Boolean(name)),
          triggeredByName: run.automated ? undefined : adminNames.get(run.triggeredBy),
        })),
      };
    },
    {
      enabled,
      refetchInterval: (data) =>
        data?.list.some((run) => isActiveReconciliationRun(run))
          ? RECONCILIATION_POLL_INTERVAL_MS
          : false,
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: 'Sorry, we failed to fetch the reconciliations. Please try again.',
        });
      },
    }
  );
};

export default useFetchReconciliationsPage;
